"use client";

import { WatcherFormField } from "@/components/Form";
import { useRobot } from "@/hooks/useRobot";
import { IRobot, IRobotStep } from "@/types/robot";
import { Button, Card } from "flowbite-react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { STEP_FORM_FIELDS, STEP_TYPE_OPTIONS } from "./const";
import { StepControl } from "./control";

export function WatcherStepSingleItem({ fieldIndex }: { fieldIndex: number }) {
  const type = useWatch({
    name: `steps.${fieldIndex}.type`,
  });
  return (
    <>
      <div className="flex flex-row align-center">
        <p className="mr-2 text-uppercase">
          <WatcherFormField
            className="w-auto"
            key="type"
            label={`${fieldIndex + 1}. Step Type`}
            type="select"
            fieldKey={`steps.${fieldIndex}.type`}
            options={STEP_TYPE_OPTIONS}
          />
        </p>
      </div>

      <div className="w-full grid grid-cols-2 gap-2">
        {(STEP_FORM_FIELDS[type] || []).map((field) => (
          <WatcherFormField
            {...field}
            key={field.key}
            fieldKey={`steps.${fieldIndex}.${field.key}`}
          />
        ))}
      </div>
    </>
  );
}

export function WatcherStepsField() {
  const methods = useFormContext<{
    steps: IRobotStep[];
  }>();

  const { control } = methods;

  const fieldControls = useFieldArray({
    name: "steps",
    control,
  });

  const { fields: stepFields = [], insert } = fieldControls;

  return (
    <>
      <p>Configure Steps</p>
      {stepFields.map((stepField, index) => (
        <div className="group relative" key={stepField.id}>
          <Card className="py-3 rounded">
            <WatcherStepSingleItem fieldIndex={index} />
          </Card>
          <StepControl index={index} controls={fieldControls} />
        </div>
      ))}
      {stepFields.length === 0 && (
        <div className="w-full flex flex-row justify-center my-5">
          <Button
            onClick={() => {
              insert(0, {
                type: "click",
                selector: "",
              });
            }}
          >
            Create A First Step
          </Button>
        </div>
      )}
    </>
  );
}

export function WatcherStepForm({
  steps,
  onSubmit,
  buttonLabel = "Save",
}: {
  steps: IRobotStep[];
  onSubmit: (values: any) => any;
  buttonLabel?: string;
}) {
  const methods = useForm({
    values: {
      steps,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    control,
  } = methods;

  const stepsValue = useWatch({
    name: "steps",
    control,
  });

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-8 pt-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <WatcherStepsField />

        {stepsValue.length !== 0 && (
          <div className="flex flex-row justify-end">
            <Button className="mt-2 px-5" disabled={!isDirty} type="submit">
              {buttonLabel}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
}
